import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import {
  authorizeConversationNotificationHandoff,
  conversationNotificationHandoffFromLocation,
} from '@/lib/conversation-notification-handoff'
import { useTRPC } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { planInitialConnectionHydration } from '@/social/dashboard-url-state'
import { useSocialOverview, useSocialWorkspace } from '@/social/social-provider'
import type { SocialOverview } from '@/social/types'
import { useSocialText } from '@/social/use-social-text'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, MessageCircle, RefreshCw } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ConversationPanel } from './conversation-panel'
import { SocialPeoplePanel } from './social-people-panel'

export function SocialDashboard({
  requestedConnectionId,
  onConnectionChange,
}: {
  requestedConnectionId?: string | null
  onConnectionChange?: (connectionId: string | null) => void
}) {
  const text = useSocialText()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const workspace = useSocialWorkspace()
  const overviewQuery = useSocialOverview(workspace.available)
  const { activeConnectionId, openConnection } = workspace
  const overview = overviewQuery.data as SocialOverview | undefined
  const handoffRequest = useMemo(
    () =>
      conversationNotificationHandoffFromLocation(
        location.pathname,
        location.search,
      ),
    [location.pathname, location.search],
  )
  const initialRequestedConnectionId = useRef(
    handoffRequest.present ? null : requestedConnectionId,
  )
  const initialUrlHydrated = useRef(false)
  const [urlHydrated, setUrlHydrated] = useState(false)
  const [handoffGate, setHandoffGate] = useState<{
    kind: 'resolving'
    token: string
  } | null>(
    handoffRequest.token
      ? { kind: 'resolving', token: handoffRequest.token }
      : null,
  )
  const handledHandoffToken = useRef<string | null>(null)
  const handoffAuthorization = useRef<{
    key: string
    promise: Promise<string | null>
  } | null>(null)
  const activeConnection
    = handoffRequest.present || handoffGate
      ? null
      : overview?.administrator?.connectionId === activeConnectionId
        ? overview.administrator
        : (overview?.administratorConversations.find(
            conversation => conversation.connectionId === activeConnectionId,
          )
          ?? overview?.friends.find(
            friend => friend.connectionId === activeConnectionId,
          )
          ?? null)

  useEffect(() => {
    if (!handoffRequest.present) {
      if (handoffGate) {
        handoffAuthorization.current = null
        setHandoffGate(null)
      }
      return
    }
    if (!handoffRequest.token) {
      openConnection(null)
      handoffAuthorization.current = null
      setHandoffGate(null)
      navigate('/dashboard?tab=social', { replace: true })
      return
    }
    if (handledHandoffToken.current === handoffRequest.token) {
      if (!handoffGate) {
        openConnection(null)
        navigate('/dashboard?tab=social', { replace: true })
      }
      return
    }
    openConnection(null)
    if (
      handoffGate?.kind === 'resolving'
      && handoffGate.token === handoffRequest.token
    ) {
      return
    }
    setHandoffGate({ kind: 'resolving', token: handoffRequest.token })
  }, [
    handoffGate,
    handoffRequest.present,
    handoffRequest.token,
    navigate,
    openConnection,
  ])

  useEffect(() => {
    if (
      handoffGate?.kind !== 'resolving'
      || !handoffRequest.present
      || handoffRequest.token !== handoffGate.token
      || !workspace.available
      || !user?.id
    ) {
      return
    }
    const { token } = handoffGate
    const recipientUserId = user.id
    const authorizationKey = `${recipientUserId}:${token}`
    handledHandoffToken.current = token
    let authorization = handoffAuthorization.current
    if (!authorization || authorization.key !== authorizationKey) {
      authorization = {
        key: authorizationKey,
        promise: authorizeConversationNotificationHandoff({
          token,
          currentUserId: recipientUserId,
          loadAuthorizedOverview: async (connectionId) => {
            const queryOptions = trpc.socialRouter.getOverview.queryOptions({
              activeConnectionId: connectionId,
            })
            await queryClient.cancelQueries({
              queryKey: queryOptions.queryKey,
              exact: true,
            })
            return queryClient.fetchQuery({
              ...queryOptions,
              staleTime: 0,
            })
          },
        }),
      }
      handoffAuthorization.current = authorization
    }
    let cancelled = false

    void authorization.promise.then((connectionId) => {
      if (cancelled || handoffAuthorization.current !== authorization)
        return
      navigate('/dashboard?tab=social', { replace: true })
      openConnection(connectionId)
      setHandoffGate(null)
      handoffAuthorization.current = null
    })

    return () => {
      cancelled = true
    }
  }, [
    handoffGate,
    handoffRequest.present,
    handoffRequest.token,
    navigate,
    openConnection,
    queryClient,
    trpc.socialRouter.getOverview,
    user?.id,
    workspace.available,
  ])

  useEffect(() => {
    if (handoffRequest.present || handoffGate)
      return
    const plan = planInitialConnectionHydration({
      workspaceAvailable: workspace.available,
      alreadyHydrated: initialUrlHydrated.current,
      requestedConnectionId: initialRequestedConnectionId.current,
      activeConnectionId,
    })
    if (!plan.markHydrated)
      return
    initialUrlHydrated.current = true
    if (plan.connectionToOpen)
      openConnection(plan.connectionToOpen)
    setUrlHydrated(true)
  }, [
    activeConnectionId,
    handoffGate,
    handoffRequest.present,
    openConnection,
    workspace.available,
  ])

  useEffect(() => {
    if (
      urlHydrated
      && !handoffRequest.present
      && !handoffGate
      && !overviewQuery.isPending
      && !overviewQuery.isFetching
      && overview
      && activeConnectionId
      && !activeConnection
    ) {
      openConnection(null)
    }
  }, [
    activeConnection,
    activeConnectionId,
    handoffGate,
    handoffRequest.present,
    openConnection,
    overview,
    overviewQuery.isFetching,
    overviewQuery.isPending,
    urlHydrated,
  ])

  useEffect(() => {
    if (
      urlHydrated
      && !handoffRequest.present
      && !handoffGate
      && requestedConnectionId !== activeConnectionId
    ) {
      onConnectionChange?.(activeConnectionId)
    }
  }, [
    activeConnectionId,
    handoffGate,
    handoffRequest.present,
    onConnectionChange,
    requestedConnectionId,
    urlHydrated,
  ])

  const selectConnection = (connectionId: string) => {
    openConnection(connectionId)
  }

  const closeConversation = () => {
    openConnection(null)
  }

  if (!workspace.available || overviewQuery.isPending) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-xl border bg-card">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (overviewQuery.isError || !overview) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 rounded-xl border bg-card px-6 text-center">
        <p className="text-sm text-destructive">
          {text('social.error', 'Something went wrong.')}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => overviewQuery.refetch()}
        >
          <RefreshCw className="size-4" />
          {text('social.retry', 'Try again')}
        </Button>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="border-b p-4 md:px-5">
        <h2 className="text-lg font-semibold">
          {text('social.title', 'Friends & messages')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {text(
            'social.description',
            'Find a known username, chat with friends, and share multiplayer rooms.',
          )}
        </p>
      </div>
      <div className="grid h-[min(720px,calc(100dvh-15rem))] min-h-[520px] md:grid-cols-[340px_minmax(0,1fr)]">
        <SocialPeoplePanel
          overview={overview}
          activeConnectionId={activeConnection?.connectionId ?? null}
          onSelectConnection={selectConnection}
          onOpenProfile={() => {
            workspace.setDockMode('minimized')
            navigate('/dashboard?tab=profile')
          }}
          className={cn(
            'border-r-0 md:block md:border-r',
            activeConnection && 'hidden',
          )}
        />

        {}
        <div
          className={cn(
            'flex min-h-0 flex-col',
            !activeConnection && 'hidden md:flex',
          )}
        >
          {activeConnection ? (
            <ConversationPanel
              key={activeConnection.connectionId}
              connectionId={activeConnection.connectionId}
              friend={activeConnection.user}
              canMuteNotifications={activeConnection.canMuteNotifications}
              notificationsMuted={activeConnection.notificationsMuted}
              currentUserId={overview.profile.id}
              onBack={closeConversation}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center px-8 text-center">
              <span className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <MessageCircle className="size-6" />
              </span>
              <h3 className="font-semibold">
                {overview.profile.isAdministrator
                  ? text(
                      'social.administrator.selectConversation',
                      'Choose a user conversation to continue chatting',
                    )
                  : text(
                      'social.conversation.select',
                      'Choose a friend to start chatting',
                    )}
              </h3>
              <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                {text(
                  'social.conversation.selectDescription',
                  'Messages stay available here and in the floating chat window.',
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
